import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  SlidersHorizontal,
  ArrowUpDown,
  PackageSearch,
  RefreshCw,
  AlertCircle,
  ChevronRight,
  ShoppingBag,
} from "lucide-react";

import { productService } from "../../services/productService";
import { CATEGORIES } from "../../constants/categories";
import ProductCard from "../components/ProductCard";

// Libellés affichés dans la page (clé = valeur Firestore)
const CATEGORY_LABELS = Object.fromEntries(
  CATEGORIES.map((c) => [c.value, c.label])
);

// Prix effectif d'un produit : variante par défaut, sinon prix de base
const effectivePrice = (product) =>
  Number(product?.variants?.[0]?.price ?? product?.price ?? 0);

export default function AllProducts({ isAll = false }) {
  const { categoryName } = useParams();

  const [products, setProducts] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("newest");
  const [minPrice, setMinPrice] = useState("0");
  const [maxPrice, setMaxPrice] = useState("10000");

  const categoryLabel = isAll
    ? "Tous les produits"
    : CATEGORY_LABELS[categoryName] || categoryName || "Produits";

  // ── Chargement depuis Firestore ─────────────────────────
  useEffect(() => {
    let cancelled = false;

    const fetchProducts = async () => {
      setLoading(true);
      setError("");

      try {
        const data = isAll
          ? await productService.getAll()
          : await productService.getByCategory(categoryName);

        if (!cancelled) {
          setProducts(Array.isArray(data) ? data.filter((p) => p.name) : []);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError("Impossible de charger les produits. Veuillez réessayer.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProducts();
    return () => {
      cancelled = true;
    };
  }, [categoryName, isAll]);

  // ── Filtres et tri ──────────────────────────────────────
  const filteredProducts = useMemo(() => {
    const min = Math.max(0, Number(minPrice) || 0);
    const max = maxPrice.trim() === "" ? Infinity : Math.max(0, Number(maxPrice));

    const result = products.filter((p) => {
      const price = effectivePrice(p);
      return price >= min && price <= max;
    });

    switch (sortBy) {
      case "price-asc":
        result.sort((a, b) => effectivePrice(a) - effectivePrice(b));
        break;
      case "price-desc":
        result.sort((a, b) => effectivePrice(b) - effectivePrice(a));
        break;
      case "name":
        result.sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""), "fr")
        );
        break;
      default:
        break;
    }

    return result;
  }, [products, minPrice, maxPrice, sortBy]);

  const resetFilters = () => {
    setMinPrice("0");
    setMaxPrice("10000");
    setSortBy("newest");
  };

  // ── États ───────────────────────────────────────────────
  if (loading) {
    return (
      <main className="flex min-h-[60vh] flex-col items-center justify-center bg-[#faf9f6] px-4">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-stone-200 border-t-[#a87942]" />
        <p className="mt-5 text-sm text-stone-500">Chargement des produits…</p>
      </main>
    );
  }

  if (error) {
    return (
      <main className="min-h-[60vh] bg-[#faf9f6] px-4 py-24">
        <div className="mx-auto max-w-lg rounded-2xl border border-red-100 bg-white p-8 text-center shadow-sm">
          <AlertCircle className="mx-auto h-10 w-10 text-red-500" />
          <h2 className="mt-4 text-xl font-semibold text-stone-900">
            Une erreur est survenue
          </h2>
          <p className="mt-2 text-sm text-stone-500">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-stone-900 px-6 py-3 text-sm font-medium text-white transition hover:bg-[#a87942]"
          >
            <RefreshCw size={16} />
            Réessayer
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#faf9f6] text-stone-900">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">

        {/* Fil d'Ariane */}
        <nav aria-label="Fil d'Ariane" className="mb-8 flex items-center gap-2 text-xs text-stone-500 sm:text-sm">
          <Link to="/" className="transition hover:text-[#a87942]">Accueil</Link>
          <ChevronRight size={14} />
          <span className="font-medium text-stone-800">{categoryLabel}</span>
        </nav>

        {/* En-tête */}
        <section className="mb-8 flex flex-col justify-between gap-5 border-b border-stone-200 pb-8 sm:mb-10 sm:flex-row sm:items-end">
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.25em] text-[#a87942]">
              Découvrez notre collection
            </p>
            <h1 className="text-3xl font-semibold tracking-tight text-stone-900 sm:text-4xl lg:text-5xl">
              {categoryLabel}
            </h1>
            <p className="mt-3 max-w-xl text-sm leading-6 text-stone-500 sm:text-base">
              Explorez notre sélection et trouvez les produits qui vous correspondent.
            </p>
          </div>

          <div className="flex w-fit items-center gap-2 rounded-full border border-stone-200 bg-white px-4 py-2.5 text-sm text-stone-600">
            <ShoppingBag size={16} className="text-[#a87942]" />
            <span>
              {filteredProducts.length} produit{filteredProducts.length !== 1 ? "s" : ""}
            </span>
          </div>
        </section>

        {/* Filtres */}
        <section className="mb-8 rounded-2xl border border-stone-200/80 bg-white p-4 sm:p-5">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-stone-800">
              <SlidersHorizontal size={18} className="text-[#a87942]" />
              Filtrer et trier
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:flex lg:items-end">
              {/* Tri */}
              <div className="min-w-0 lg:min-w-52">
                <label htmlFor="sort-products" className="mb-2 block text-xs font-medium uppercase tracking-wider text-stone-500">
                  Trier par
                </label>
                <div className="relative">
                  <ArrowUpDown size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                  <select
                    id="sort-products"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full appearance-none rounded-xl border border-stone-200 bg-[#faf9f6] py-3 pl-9 pr-8 text-sm text-stone-700 outline-none transition focus:border-[#a87942] focus:ring-2 focus:ring-[#a87942]/10"
                  >
                    <option value="newest">Nouveautés</option>
                    <option value="price-asc">Prix croissant</option>
                    <option value="price-desc">Prix décroissant</option>
                    <option value="name">Nom (A-Z)</option>
                  </select>
                </div>
              </div>

              {/* Budget */}
              <div>
                <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-stone-500">
                  Budget (MAD)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    aria-label="Prix minimum"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    className="w-full min-w-0 rounded-xl border border-stone-200 bg-[#faf9f6] px-3 py-3 text-sm outline-none transition focus:border-[#a87942] sm:w-28"
                    placeholder="Min."
                  />
                  <span className="text-stone-400">—</span>
                  <input
                    type="number"
                    min="0"
                    aria-label="Prix maximum"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    className="w-full min-w-0 rounded-xl border border-stone-200 bg-[#faf9f6] px-3 py-3 text-sm outline-none transition focus:border-[#a87942] sm:w-28"
                    placeholder="Max."
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={resetFilters}
                className="self-end rounded-xl px-3 py-3 text-sm font-medium text-stone-500 transition hover:bg-stone-100 hover:text-stone-900"
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </section>

        {/* Liste */}
        {filteredProducts.length === 0 ? (
          <section className="rounded-3xl border border-dashed border-stone-300 bg-white px-5 py-16 text-center sm:py-24">
            <PackageSearch size={44} strokeWidth={1.3} className="mx-auto text-stone-300" />
            <h2 className="mt-5 text-xl font-semibold text-stone-800">Aucun produit trouvé</h2>
            <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-stone-500">
              Aucun produit ne correspond à votre budget. Essayez de modifier les filtres.
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-6 rounded-full bg-stone-900 px-6 py-3 text-sm font-medium text-white transition hover:bg-[#a87942]"
            >
              Afficher tous les produits
            </button>
          </section>
        ) : (
          <section
            aria-label="Catalogue des produits"
            className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          >
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.2) }}
                className="min-w-0"
              >
                <ProductCard p={product} />
              </motion.div>
            ))}
          </section>
        )}

        {filteredProducts.length > 0 && (
          <p className="mt-10 text-center text-xs text-stone-400">
            Affichage de {filteredProducts.length} produit{filteredProducts.length !== 1 ? "s" : ""} sur {products.length}
          </p>
        )}
      </div>
    </main>
  );
}